import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import adminApiClient from '../../api/adminAxios';

const STATE_COLORS = {
  ACTIVE: 'bg-emerald-900 text-emerald-300 border-emerald-700',
  GRACE: 'bg-amber-900 text-amber-300 border-amber-700',
  READ_ONLY: 'bg-orange-900 text-orange-300 border-orange-700',
  DORMANT: 'bg-red-900 text-red-300 border-red-700',
};

function StatePill({ state }) {
  const cls = STATE_COLORS[state] || 'bg-slate-700 text-slate-300 border-slate-600';
  return <span className={`inline-block px-2 py-0.5 rounded text-xs border ${cls}`}>{state}</span>;
}

function AdminConsole() {
  const auth = useAdminAuth();
  const navigate = useNavigate();

  const [overview, setOverview] = useState(null);
  const [subscribers, setSubscribers] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('id');
  const [sortDir, setSortDir] = useState('asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const size = 20;

  useEffect(() => {
    adminApiClient.get('/api/admin/overview').then((res) => setOverview(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setError('');
    adminApiClient
      .get('/api/admin/subscribers', { params: { page, size, search: search || undefined, sortBy, sortDir } })
      .then((res) => setSubscribers(res.data))
      .catch(() => setError('Failed to load subscribers.'))
      .finally(() => setLoading(false));
  }, [page, search, sortBy, sortDir]);

  const toggleSort = (field) => {
    setPage(0);
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const handleLogout = () => {
    auth.logout();
    navigate('/iseeallnomisses');
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <h1 className="text-base font-semibold">Platform Admin — {auth.username}</h1>
        <button onClick={handleLogout} className="text-sm text-slate-400 hover:text-slate-100">
          Log out
        </button>
      </header>

      <main className="p-6 max-w-6xl mx-auto">
        {overview && (
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <StatCard label="Active staff (platform)" value={overview.totalActiveStaff} />
            <StatCard label="Implied MRR (₹)" value={overview.impliedMrr} note={overview.impliedMrrNote} />
            <StatCard label="WhatsApp usage (this month)" value={overview.whatsappUsageThisMonth} />
            <StatCard
              label="Restaurants by state"
              value={Object.entries(overview.restaurantCountByState)
                .map(([k, v]) => `${k}: ${v}`)
                .join(', ')}
              small
            />
          </section>
        )}

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-300">Subscribers</h2>
            <input
              type="text"
              placeholder="Search name or owner…"
              value={search}
              onChange={(e) => {
                setPage(0);
                setSearch(e.target.value);
              }}
              className="px-3 py-1.5 rounded border border-slate-700 bg-slate-800 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-slate-500"
            />
          </div>

          {error && <p className="text-sm text-red-400 mb-3">{error}</p>}

          <div className="overflow-x-auto rounded border border-slate-800">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-800 text-slate-400 text-left">
                <tr>
                  <SortableHeader field="name" label="Restaurant" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <th className="px-3 py-2">Owner</th>
                  <SortableHeader field="tier" label="Tier" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <SortableHeader field="state" label="State" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <th className="px-3 py-2">Expires</th>
                  <SortableHeader
                    field="activeStaffCount"
                    label="Active staff"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    field="whatsappOutboundThisMonth"
                    label="WA out (mo.)"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    field="whatsappInboundThisMonth"
                    label="WA in (mo.)"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={toggleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-3 py-4 text-center text-slate-500">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && subscribers?.content?.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-4 text-center text-slate-500">
                      No subscribers found.
                    </td>
                  </tr>
                )}
                {!loading &&
                  subscribers?.content?.map((s) => (
                    <tr
                      key={s.id}
                      className="border-t border-slate-800 hover:bg-slate-800/60 cursor-pointer"
                      onClick={() => navigate(`/iseeallnomisses/restaurants/${s.id}`)}
                    >
                      <td className="px-3 py-2">
                        <Link
                          to={`/iseeallnomisses/restaurants/${s.id}`}
                          className="text-slate-100 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {s.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-slate-400">
                        {s.ownerName || '—'}
                        {s.ownerEmail ? ` (${s.ownerEmail})` : ''}
                      </td>
                      <td className="px-3 py-2">{s.tier || '—'}</td>
                      <td className="px-3 py-2">
                        <StatePill state={s.state} />
                      </td>
                      <td className="px-3 py-2 text-slate-400">
                        {s.subscriptionExpiresAt ? s.subscriptionExpiresAt.replace('T', ' ') : 'never'}
                      </td>
                      <td className="px-3 py-2">{s.activeStaffCount}</td>
                      <td className="px-3 py-2">{s.whatsappOutboundThisMonth}</td>
                      <td className="px-3 py-2">{s.whatsappInboundThisMonth}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {subscribers && (
            <div className="flex items-center justify-between mt-3 text-sm text-slate-400">
              <span>
                Page {subscribers.page + 1} of {Math.max(subscribers.totalPages, 1)} ({subscribers.totalElements}{' '}
                total)
              </span>
              <div className="flex gap-2">
                <button
                  disabled={!subscribers.hasPrevious}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="px-2 py-1 rounded border border-slate-700 disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  disabled={!subscribers.hasNext}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-2 py-1 rounded border border-slate-700 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function SortableHeader({ field, label, sortBy, sortDir, onSort }) {
  const active = sortBy === field;
  return (
    <th
      className="px-3 py-2 cursor-pointer select-none hover:text-slate-200"
      onClick={() => onSort(field)}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      <span className="ml-1 text-slate-600">{active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </th>
  );
}

function StatCard({ label, value, note, small }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-800/50 p-4">
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      <p className={small ? 'text-sm text-slate-100' : 'text-2xl font-semibold text-slate-100'}>{value}</p>
      {note && <p className="text-[11px] text-slate-500 mt-1">{note}</p>}
    </div>
  );
}

export default AdminConsole;
